<template>
    <div class="detail" :class="$mq">
        <div class="top-actions">
            <div class="return-btn" @click="goBackToTemplatesList"></div>
            <div class="titl">
                <h2 class="name">{{ title }}</h2>
            </div>

            <input v-if="!hasError" type="text" class="search" v-model="search" :placeholder="$t('vtf.datatable.search')" />
            <div @click="editColumns" class="edit-columns"></div>
            <div @click="emitAddEdit('add')" v-if="this.userAppAccess && checkPermissions()" class="add-report"></div>
        </div>

        <!-- TABS (DRAFT/COMPLETED) -->
        <div class="tabs-container">
            <div class="tabs">
                <button class="tab" :class="{ active: activeTab === 'draft' }" @click="switchTab('draft')">
                    <div v-if="draftRecords && draftRecords.length > 0" class="icon-container">
                        <i class="i-andy-circle-exclamation i-andy-s"></i>
                    </div>
                    <span class="tab-text"> {{ $t('register.tabs.draft') }} </span>
                    <span class="tab-text" v-if="draftRecords && draftRecords.length > 0">({{ totalDraftRecords }})</span>
                </button>
                <button class="tab" :class="{ active: activeTab === 'completed' }" @click="switchTab('completed')">
                    <span class="tab-text">{{ $t('register.tabs.completed') }}</span>
                </button>
            </div>
        </div>

        <div class="content hide-scrollbar" :class="{ 'no-scroll': filteredTableData.length === 0 && !hasError }">
            <!-- TABLE -->
            <div class="datatable-container" id="datatable" v-if="filteredTableData.length > 0 && (draftRecords.length > 0 || completedRecords.length > 0)">
                <DataTable @sort="sortTable" :headers="headers" :tableItems="filteredTableData" @emitAddEdit="(id) => emitAddEdit(id)" tool="Records" />
            </div>

            <!-- PAGINATION BUTTONS for DRAFT tab (botones de paginación para la pestaña BORRADOR) -->
            <div class="pagination" v-if="shouldShowPagination.draft">
                <!-- GO ONE PAGE BACK BUTTON-->
                <button v-if="tabPages.draft > 1" @click="goToPreviousPage" class="item prev"></button>
                <!-- GO ONE PAGE FORWARD BUTTON -->
                <button v-if="draftHasNextPage" @click="goToNextPage" class="item next"></button>
            </div>

            <!-- PAGINATION BUTTONS for COMPLETED tab (botones de paginación para la pestaña COMPLETADO) -->
            <div class="pagination" v-if="shouldShowPagination.completed">
                <button v-if="tabPages.completed > 1" @click="goToPreviousPage" class="item prev"></button>
                <button v-if="completedHasNextPage" @click="goToNextPage" class="item next"></button>
            </div>

            <template v-else-if="filteredTableData.length === 0">
                <template v-if="!hasError && connection && recordsAreLoaded">
                    <EmptyPage v-if="activeTab === 'draft'" @buttonAction="emitAddEdit('add')" :title="$t('empty_pages.records_draft.title')" :description="$t('empty_pages.records_draft.description')" :buttonText="$t('empty_pages.records_draft.create_record')" type="record" />

                    <EmptyPage v-else :title="$t('empty_pages.records_completed.title')" :description="$t('empty_pages.records_completed.description')" type="record" />
                </template>

                <OfflineAlert v-else-if="!isOnline && !recordsAreLoaded && !isLoading" gType="registerConnection" @reload="loadPageData" />
            </template>

            <EmptyPage v-if="hasError" :buttonText="$t('empty_pages.return_logs')" @buttonAction="goBackToTemplatesList" buttonClass="back" :type="'error'" :title="$t('assets.overlay.timeout.message')" :description="$t('empty_pages.error_record')" />
        </div>
    </div>
</template>

<script>
import DataTable from '../ui/DataTable.vue'
import EmptyPage from '@/components/layout/EmptyPage'
import { FORMAT } from '@/constants'
import OfflineAlert from '@/components/domain/ui/Box.vue'
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'

export default {
    components: { EmptyPage, DataTable, OfflineAlert },
    props: {
        records: { type: Object },
        hasError: { type: undefined, default: false },
        detailId: { type: undefined, default: null }
    },
    data() {
        // Leer estado de tabs guardado desde localStorage
        const savedTabState = localStorage.getItem(`tabState_${this.$route.params.id}`)
        const parsedTabState = savedTabState ? JSON.parse(savedTabState) : null

        return {
            search: '',
            pagination: {
                sortBy: 'dateUnix',
                rowsPerPage: -1,
                descending: true
            },
            hidden_columns: localStorage.col_register ? JSON.parse(localStorage.col_register) : [],
            FORMAT: FORMAT,
            itemsPerPage: 20,
            // name of the column to sort by DESC:
            columnSortedDesc: false,
            // name of the column to sort by ASC:
            columnSortedAsc: false,
            headersConf: localStorage.getItem(`headersConf_${this.detailId}`) ? JSON.parse(localStorage.getItem(`headersConf_${this.detailId}`)) : [],
            isOnline: true,
            // Inicializar activeTab y tabPages desde localStorage o valores por defecto
            activeTab: (parsedTabState && parsedTabState.activeTab) || 'draft',
            tabPages: {
                draft: (parsedTabState && parsedTabState.tabPages && parsedTabState.tabPages.draft) || 1,
                completed: (parsedTabState && parsedTabState.tabPages && parsedTabState.tabPages.completed) || 1
            },
            searchResults: null,
            searchTimeout: null
        }
    },
    created() {
        // Aplicar ordenamiento inicial por fecha (descendente) según la configuración de pagination
        if (this.pagination.sortBy === 'dateUnix' && this.pagination.descending) {
            this.columnSortedDesc = 'date'
        }
    },
    mounted() {
        this.isOnline = fnCheckConnection()
    },
    computed: {
        isLoading() {
            return this.$store.getters['register/getIsLoading']
        },
        // listado de registros en borrador (máximo 20 por página)
        draftRecords() {
            return this.$store.getters['register/getDraftRecords']
        },
        // número total de registros en borrador para un template de registros
        totalDraftRecords() {
            return this.$store.getters['register/getTotalDraftRecords']
        },
        // listado de registros completados (máximo 20 por página)
        completedRecords() {
            return this.$store.getters['register/getCompletedRecords']
        },
        // número total de registros completados para un template de registros
        totalCompletedRecords() {
            return this.$store.getters['register/getTotalCompletedRecords']
        },
        // listado de registros para la tab activa (máximo 20 por página)
        activeTabRecords() {
            return this.activeTab === 'draft' ? this.draftRecords : this.completedRecords
        },
        // listado de ids de los registros para la tab activa (máximo 20 por página)
        activeTabRecordsHashIDs() {
            return this.activeTabRecords.map((record) => record.id)
        },
        filteredTableData() {
            const self = this

            // Si no hay búsqueda, devolver directamente el listado de registros ya filtrado por status
            if (!self.search) {
                return this.fillTable(this.activeTabRecords, this.activeTabRecordsHashIDs)
            }

            // Si hay una búsqueda, usar el resultado del filtrado asíncrono si está disponible
            if (this.searchResults) {
                const hashIDs = this.searchResults ? this.searchResults.map((record) => record.id) : false

                return this.fillTable(this.searchResults, hashIDs)
            }

            // Si no hay resultados filtrados aún, mostrar todos los registros, según la tab activa
            return this.fillTable(this.activeTabRecords, this.activeTabRecordsHashIDs)
        },
        connection() {
            return this.$store.getters['getConnState']
        },
        recordsAreLoaded() {
            return this.$store.getters['register/getRecordsAreLoaded']
        },
        user_id() {
            return this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            var employee = this.$store.getters['employee/getEmployee'](this.user_id)
            if (employee) {
                return employee
            }
        },
        userAppAccess() {
            return this.$store.getters['loginUser/getHasAccessApp']
        },
        title() {
            if (Object.keys(this.records).length > 0 && fnCheckConnection()) {
                let keys = Object.keys(this.records)
                return keys.length > 0 ? this.records[keys[0]].title : ''
            } else {
                var template = this.$store.getters['register/getTemplateById'](this.$route.params.id)
                if (template) {
                    return template.name
                }
                return ''
            }
        },
        template() {
            return this.$store.getters['register/getTemplateById'](this.$route.params.id)
        },
        headers: {
            get() {
                const iMaxColumnsVisibles = 10
                const schema = this.$store.getters['register/getHeaders']

                const headers = []

                headers.push({
                    text: this.$t('config.status.title'),
                    value: 'status',
                    sortable: false,
                    visible: true,
                    blocked: true,
                    width: '132px'
                })

                headers.push({
                    text: this.$t('register.headers.date'),
                    value: 'date',
                    sortable: true,
                    visible: true,
                    blocked: true,
                    width: '300px'
                })

                headers.push({
                    text: this.$t('register.headers.employee'),
                    value: 'employee',
                    sortable: true,
                    visible: true,
                    blocked: true
                })

                const schemaValues = Object.values(schema)

                if (schemaValues.length > 0) {
                    const titlesSchema = schemaValues.filter((header) => header.type !== 8 && header.type !== 9).map((value) => value)
                    titlesSchema.forEach((header) => {
                        headers.push({
                            text: header.title,
                            value: header.id,
                            sortable: true,
                            type: header.type,
                            visible: true,
                            blocked: false
                        })
                    })
                }

                if (this.headersConf.length > 0) {
                    if (this.headersConf.length !== headers.length) {
                        this.headersConf.forEach((headerConf) => {
                            const index = headers.findIndex((header) => header.value === headerConf.value)
                            if (index !== -1) {
                                headers[index].visible = headerConf.visible
                            }
                        })
                        return JSON.parse(JSON.stringify(headers))
                    }
                    return JSON.parse(JSON.stringify(this.headersConf))
                }

                headers.forEach((header, index) => {
                    header.visible = index < iMaxColumnsVisibles
                })

                return headers
            },
            set(val) {
                //this.headers = val
            }
        },
        totalDraftRecords() {
            const totalDraftRecords = this.$store.getters['register/getTotalDraftRecords']
            return totalDraftRecords ? totalDraftRecords : 0
        },
        totalCompletedRecords() {
            const totalCompletedRecords = this.$store.getters['register/getTotalCompletedRecords']
            return totalCompletedRecords ? totalCompletedRecords : 0
        },
        previousEditId() {
            return this.$store.getters['register/getPreviousEditId']
        },
        shouldShowPagination() {
            const hasBasicRequirements = this.connection && this.filteredTableData.length > 0

            if (!hasBasicRequirements) {
                return { draft: false, completed: false }
            }

            return {
                draft: this.activeTab === 'draft',
                completed: this.activeTab === 'completed'
            }
        },
        draftHasNextPage() {
            return this.itemsPerPage * this.tabPages.draft < this.totalDraftRecords
        },
        completedHasNextPage() {
            return this.itemsPerPage * this.tabPages.completed < this.totalCompletedRecords
        }
    },
    methods: {
        async filterRecordsBySearch(activeTabRecords) {
            const self = this

            return new Promise((resolve) => {
                // Cancelar timeout anterior si existe
                if (self.searchTimeout) {
                    clearTimeout(self.searchTimeout)
                }

                self.searchTimeout = setTimeout(() => {
                    const filteredRecordsBySearch = activeTabRecords.filter((record) => {
                        const searchTerm = self.normalizeText(self.search)

                        // Verificar que el registro tenga items
                        if (!record.items) return false

                        // Buscar en todos los items del registro
                        for (let itemid in record.items) {
                            const item = record.items[itemid]
                            const itemValue = item.value

                            // Verificar si el item es válido para búsqueda
                            if (itemValue === null || itemValue === undefined || item.type === 14 || item.type === 5) continue

                            // Verificar si el valor contiene el término de búsqueda
                            const valueString = self.normalizeText(itemValue.toString().toLowerCase())
                            if (!valueString.includes(searchTerm)) continue

                            // Si llegamos aquí, encontramos una coincidencia válida
                            return true
                        }

                        // Si llegamos aquí, no encontramos coincidencias
                        return false
                    })

                    // Actualizar los resultados filtrados
                    self.searchResults = filteredRecordsBySearch
                    resolve(filteredRecordsBySearch)
                }, 300)
            })
        },
        normalizeText(text) {
            if (!text) return ''

            return text
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos (tildes)
        },
        switchTab(tab) {
            if (this.activeTab === tab) {
                // no hacer nada si se hace click de nuevo en la tab activa
                return
            }

            this.activeTab = tab

            // Guardar el estado actualizado en localStorage
            const tabState = {
                activeTab: this.activeTab,
                tabPages: this.tabPages
            }

            localStorage.setItem(`tabState_${this.$route.params.id}`, JSON.stringify(tabState))

            this.search = '' // Clear search when switching tabs
        },
        checkPermissions() {
            const template = this.template
            const userRole = this.$store.getters['employee/getRol'](this.user.rol)
            if (userRole.level >= 8) {
                return true
            }

            if (template && template.config && !(template.config.nRoles || template.config.roles)) {
                return true
            }

            if (template && template.config && template.config.nRoles) {
                const nRoles = template.config.nRoles
                let isValid = false

                nRoles.forEach((role) => {
                    if (role.roleId == userRole.id && role.permissions.includes('w')) {
                        isValid = true
                        return
                    }
                })
                return isValid
            }

            return true
        },
        editColumns() {
            const self = this
            const headersCopy = JSON.parse(JSON.stringify(this.headers))
            this.$popup.tableColumn({
                title: this.$t('supervise.custom_columns_popup.title'),
                textSave: this.$t('button.save'),
                headers: this.headers,
                routePop: self.$route.name,
                callSave: (obj) => {
                    self.headersConf = obj.$data.headers
                    localStorage.setItem(`headersConf_${self.detailId}`, JSON.stringify(obj.$data.headers))
                    self.$popup.close()
                    self.$forceUpdate()
                },
                headers: headersCopy,
                callCancel: function () {
                    this.$popup.close()
                }
            })
        },
        sortTable(obj) {
            this.columnSortedDesc = obj.columnSortedDesc
            this.columnSortedAsc = obj.columnSortedAsc
        },
        // commented out because it's not called
        // prepareTable(records) {
        //     var values = []
        //     for (var i in records) {
        //         var raw = records[i]
        //         var row = {}
        //         // row.edit = "";
        //         // row.select = "";
        //         // row.dateUnix = raw.created_date ? moment(raw.created_date).unix() : '-'
        //         row.date = raw.created_date
        //             ? moment(raw.created_date).format(this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY HH:mm')
        //             : '-'

        //         row.employee = raw.employee && raw.employee != null ? raw.employee.name : '-'
        //         row.id = raw.id ? raw.id : ''
        //         row.arrow = raw.status ? 1 : 0
        //         row.status = raw.status
        //         row.statusB = raw.status

        //         for (var j in raw.items) {
        //             var item = raw.items[j]
        //             row[item.item_id + '_type'] = item.type
        //             row[item.item_id] = item.value
        //             if (item.type === 11) {
        //                 row[item.item_id + '_config'] = item.config ? item.config : ''
        //             }
        //         }

        //         values.push(row)
        //     }

        //     return values
        // },
        fillTable(recordsArray, index = false) {
            const self = this
            let values = []

            recordsArray.forEach((raw) => {
                if (!index || index.includes(raw.id)) {
                    const row = {}

                    row.dateUnix = raw.created_date ? moment(raw.created_date).unix() : '-'

                    row.date = raw.created_date ? '<span style="display: none">' + moment(raw.created_date).unix() + '</span>' + `<span class="bold ${this.$mq} ${raw.requireSync ? 'require-sync-icon' : ''}">` + formatDisplayDateFromStore(raw.created_date, { includeTime: true, emptyValue: '' }) + '</span>' : '-'

                    /* row.employee = raw.employee && raw.employee != null ? '<div class="img-container"> <img class="main-img" src="' + css_image_url + 'avatar/' + raw.employee.avatar + '" /><div class="name">' + raw.employee.name + '</div> </div>' : '-' */
                    if (raw.employee && raw.employee.avatar && raw.employee.avatar.includes('http')) {
                        row.employee = '<div class="img-container"> <img class="main-img" src="' + raw.employee.avatar + '" /><div class="name">' + raw.employee.name + '</div> </div>'
                    } else if (raw.employee && raw.employee.avatar && raw.employee.avatar.includes('file')) {
                        row.employee = '<div class="img-container"> <img class="main-img" src="' + raw.employee.avatar + '" /><div class="name">' + raw.employee.name + '</div> </div>'
                    } else if (raw.employee && raw.employee.avatar && raw.employee.avatar.includes('svg')) {
                        row.employee = '<div class="img-container"> <img class="main-img" src="' + css_image_url + 'avatar/' + raw.employee.avatar + '" /><div class="name">' + raw.employee.name + '</div> </div>'
                    }
                    row.id = raw.id ? raw.id : ''
                    row.arrow = raw.status ? '<div class="arrow-container"> <img class="arrow-img" src="' + css_image_url + 'right_neutro.svg" />' : '<div class="arrow-container"> <div class="bullet-img"> </div> <img class="arrow-img" src="' + css_image_url + 'right_second.svg" />'
                    row.status = raw.status
                    row.requireSync = raw.requireSync ? raw.requireSync : false

                    for (var j in raw.items) {
                        var item = raw.items[j]
                        row[item.item_id + '_type'] = item.type
                        row[item.item_id] = item.value
                        if (item.type === 11) {
                            row[item.item_id + '_config'] = item.config ? item.config : ''
                        }
                    }

                    values.push(row)
                }
            })

            // SORT FOR COLUMNS ASC DESC
            if (self.columnSortedDesc && !self.columnSortedAsc) {
                values = values.sort(function (a, b) {
                    if (b[self.columnSortedDesc] && isNaN(b[self.columnSortedDesc]) && a[self.columnSortedDesc] && isNaN(a[self.columnSortedDesc])) {
                        // Para strings: orden descendente (b compare con a)
                        return b[self.columnSortedDesc].localeCompare(a[self.columnSortedDesc])
                    } else {
                        // Para números: orden descendente (b - a)
                        return (b[self.columnSortedDesc] !== null ? b[self.columnSortedDesc] : -Infinity) - (a[self.columnSortedDesc] !== null ? a[self.columnSortedDesc] : -Infinity)
                    }
                })
            } else if (self.columnSortedAsc) {
                values = values.sort(function (a, b) {
                    if (b[self.columnSortedAsc] && isNaN(b[self.columnSortedAsc]) && a[self.columnSortedAsc] && isNaN(a[self.columnSortedAsc])) {
                        // Para strings: orden ascendente (a compare con b)
                        return a[self.columnSortedAsc].localeCompare(b[self.columnSortedAsc])
                    } else {
                        // Para números: orden ascendente (a - b)
                        return (a[self.columnSortedAsc] !== null ? a[self.columnSortedAsc] : -Infinity) - (b[self.columnSortedAsc] !== null ? b[self.columnSortedAsc] : -Infinity)
                    }
                })
            }

            return values
        },
        loadPageData() {
            if (fnCheckConnection()) {
                const self = this

                const params = {
                    id: this.$route.params.id,
                    number_pages: this.tabPages[this.activeTab],
                    number_items: this.itemsPerPage,
                    activeTab: this.activeTab
                }

                self.$store
                    .dispatch('register/loadPageRecordsByTab', params)
                    .then()
                    .catch((error) => {
                        console.error('error in dispatch for loadPageData. Error:', error)
                    })
            }
        },
        scrollDatataTable() {
            setTimeout(() => {
                document.getElementById('datatable').scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
            }, 50)
        },
        goToPreviousPage() {
            this.search = ''

            if (this.activeTab === 'draft') {
                this.tabPages.draft -= 1
            }

            if (this.activeTab === 'completed') {
                this.tabPages.completed -= 1
            }

            // Actualizar localStorage
            const tabState = {
                activeTab: this.activeTab,
                tabPages: this.tabPages
            }
            localStorage.setItem(`tabState_${this.$route.params.id}`, JSON.stringify(tabState))

            this.loadPageData()
            this.scrollDatataTable()
        },
        goToNextPage() {
            this.search = ''

            if (this.activeTab === 'draft') {
                this.tabPages.draft += 1
            }

            if (this.activeTab === 'completed') {
                this.tabPages.completed += 1
            }

            // Actualizar localStorage
            const tabState = {
                activeTab: this.activeTab,
                tabPages: this.tabPages
            }
            localStorage.setItem(`tabState_${this.$route.params.id}`, JSON.stringify(tabState))

            this.loadPageData()
            this.scrollDatataTable()
        },
        // para volver al listado de templates de registros
        goBackToTemplatesList() {
            this.tabPages.draft = 1
            this.tabPages.completed = 1
            // Limpiar el estado guardado en localStorage
            localStorage.removeItem(`tabState_${this.$route.params.id}`)

            this.$store.commit('register/setTotalDraftRecords', 0)
            this.$store.commit('register/setTotalCompletedRecords', 0)

            this.$emit('goBack')
        },
        filterColumns() {
            var self = this
            this.$filter({
                list: this.headers,
                selected: self.hidden_columns,
                callback: function (d) {
                    self.hidden_columns = [...d]
                    self.saveFilter(self.hidden_columns)
                }
            })
            this.$filter.show()
        },

        saveFilter(filter) {
            if (localStorage) {
                localStorage.col_register = JSON.stringify(filter)
            }
        },
        changeSort(column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },
        emitAddEdit(id) {
            // guardar en localStorage la tab actual y página actual para que vuelva al mismo listado luego de ver un registro
            const tabState = {
                activeTab: this.activeTab,
                tabPages: this.tabPages
            }
            localStorage.setItem(`tabState_${this.$route.params.id}`, JSON.stringify(tabState))

            this.$emit('addEdit', id)
        },
        format(value, type) {
            switch (parseInt(type)) {
                case FORMAT.CHECK:
                case FORMAT.YESNO:
                case FORMAT.YESNONC:
                    return value

                case FORMAT.DATE:
                    return formatDisplayDateFromStore(value, { includeTime: false, emptyValue: '' })

                case FORMAT.DATETIME:
                    return value ? formatDisplayDateFromStore(value, { includeTime: true, emptyValue: '' }) : ''

                case FORMAT.PHOTO:
                case FORMAT.SIGNATURE:
                    if (value && typeof value === 'object') {
                        var html = ''
                        Object.values(value).forEach((img) => {
                            html += '<img src="' + img + '"/>'
                        })
                        return value ? html : value
                    } else {
                        return value ? '<img src="' + value + '"/>' : value
                    }

                default:
                    return value
            }
        },
        preview(e, value, type) {
            if (type != undefined) {
                e.preventDefault()
                e.stopPropagation()
            }
            switch (parseInt(type)) {
                case FORMAT.PHOTO:
                case FORMAT.SIGNATURE:
                    this.$popup.media({ image: value })
                    break
                default:
                    break
            }
        }
    },
    beforeDestroy() {
        // Limpiar el timeout cuando el componente se destruya
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    },
    watch: {
        search: {
            handler(newVal) {
                // Limpiar resultados filtrados cuando no hay búsqueda
                if (!newVal) {
                    this.searchResults = null
                    return
                }

                // Obtener los registros a mostrar según la tab activa
                this.filterRecordsBySearch(this.activeTabRecords)
            }
        },
        isLoading(newVal, oldVal) {
            newVal === false ? this.$overlay.hide() : this.$overlay.loading()
        },
        draftRecords(newVal, oldVal) {
            if (newVal.length === 0 && this.completedRecords.length > 0) {
                this.activeTab = 'completed'
            }
        }
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
#content.register {
    $column-width: 0%;
    $column-max-width: 0px;
    $content-width: 100%;

    .img-container {
        @include justify-content();
        @include display-flex();
        width: max-content;

        .main-img {
            width: 30px;
            height: 30px;
        }
        .arrow-img {
            background-position: center right;
            width: 15px;
            height: 15px;
            margin-left: 35px;
            padding: 12px;
        }

        .name {
            @include justify-content();
            align-self: center;
            padding-left: 10px;
        }
        .bullet-img {
            @include align-self(center);
            @include border-radius(50%);
            background-color: $warning;
            width: 15px;
            height: 15px;
            padding-right: 10px;
        }
    }
    .arrow-container {
        @include justify-content(center);
        @include display-flex();
        width: auto;

        .arrow-img {
            background-position: center right;
            width: 15px;
            height: 15px;
            margin-left: 15px;
        }

        .bullet-img {
            @include align-self(center);
            @include border-radius(50%);
            background-color: $warning;
            width: 15px;
            height: 15px;
            padding-right: 10px;
            position: relative;
            min-width: 15px;
            left: 6px;
        }
    }

    > .content {
        padding-top: 0 !important;

        .detail {
            width: 100%;
            height: calc(100%);

            .tabs-container {
                width: 100%;
                height: fit-content;
                display: grid;
                place-items: center;
                padding-bottom: 8px;
            }

            .tabs {
                height: 44px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                align-items: center;
                width: 100%;
                background-color: white;
                padding: 4px;
                gap: 4px;
                border-radius: 4px;

                .tab {
                    @include display-flex();
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    border-radius: 4px;
                    color: #727477;
                    gap: 6px;

                    background-color: white;

                    .tab-text {
                        font-family: $text;
                    }

                    &.active {
                        background-color: #d7dae3;

                        .tab-text {
                            font-family: $text-bold;
                        }
                    }
                }

                .tab:hover {
                    background-color: white;
                    cursor: pointer;

                    &.active:hover {
                        background-color: #d7dae3;
                    }
                }

                .icon-container {
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background-color: white;

                    .i-andy-circle-exclamation {
                        color: #ffcd1a;
                    }
                }
            }

            .content {
                overflow: auto;
                height: calc(100% - (60px + 52px)); // 52px es el height de las tabs + padding bottom
            }

            .top-actions {
                @include display-flex();
                @include align-items(center);
                $width-search: 300px;
                $width-title: calc(100% - #{$width-search});
                // margin: 6px 0 10px 0;
                overflow: hidden;
                // padding: 12px;
                // padding-bottom: 10px;
                // height: 62px;
                padding-right: 0px;
                padding-bottom: 16px;
                justify-content: space-between;

                .return-btn {
                    @extend .interaction;
                    @include background($image: img('left_dark.svg'), $size: 20px);
                    @include border-radius(4px);
                    @include align-self(center);
                    background-color: #fff;
                    width: 40px;
                    height: 40px;
                    min-width: 40px;
                }
                .search {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('search_neutro.svg'), $size: 25px, $position: left 12px center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 100%;
                    background-color: #fff;
                    padding: 0 60px 0 40px;
                    border-bottom: none;
                    font-family: $text;
                    cursor: text;
                    margin: 0px;
                    margin-right: 8px;
                }

                div.edit-columns {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('settings.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #fff;
                    font-family: $text;
                    cursor: pointer;
                    margin-right: 8px;
                }

                .add-report {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('add_ffffff.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #1a9980;
                    font-family: $text;
                    cursor: pointer;
                }

                .titl {
                    @include align-self(center);
                    width: $width-title;
                    max-height: 80px;
                    height: auto;

                    .name {
                        // @include font-size(ml);
                        // @include font-size(m);
                        font-size: 18px;
                        line-height: 130%;
                        margin: 0;
                        font-family: $text-bold;
                        color: $neutro-s80;
                        width: calc(100% - 50px);
                        max-height: calc(100% - 24px);
                        display: inline-block;
                        padding-left: 8px;

                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 2;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    .subname {
                        @include font-size(sm);
                        width: calc(100% - 50px);
                        padding: 6px 6px 0 6px;
                        display: inline-block;
                        color: #999;
                    }
                }

                .search {
                    float: right;
                    width: $width-search;
                }
            }
            .datatable-container {
                padding: 0px 0px 6px 12px;
                height: auto;
                overflow-x: auto;
                overflow-y: scroll;
            }
            .pagination {
                width: 100%;
                padding: 8px 0px 0px;
                padding-left: 12px;
                display: block;
                grid-column: 1 / 4;
                height: 45px;

                .item {
                    width: calc(50% - 10px);
                    // height: 45px;
                    height: 100%;
                    max-height: 100%;
                    float: left;
                    @include border-radius(4px);
                    @extend .interaction;

                    &.next {
                        @include background($image: img('right_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                        float: right;
                    }
                    &.prev {
                        @include background($image: img('left_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                    }
                }
            }
        }
    }
    .detail.desktop {
        .arrow-img {
            margin-left: 35px;
        }
    }
    .detail.landscape {
        // height: calc(100% - 8px);

        .arrow-img {
            margin-left: 35px;
        }
    }
}
</style>

<!-- PORTRAIT STYLES -->
<style lang="scss">
#content.register {
    > .content {
        // overflow: hidden !important;
        .detail.portrait {
            .top-actions {
                width: 100%;

                div.edit-columns {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('settings.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    margin-left: auto;
                    margin-right: 5px !important;
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #fff;
                    font-family: $text;
                    cursor: pointer;
                    margin: 0;
                }

                .add-report {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('add_ffffff.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #1a9980;
                    font-family: $text;
                    cursor: pointer;
                    margin-left: 8px;
                    margin-right: 0;
                }

                .titl {
                    @include display-flex();
                    padding: 0;
                    min-width: calc(100% - 138px);
                    max-width: 85vw;
                    display: grid;
                    justify-items: start;
                    align-items: end;
                    flex: auto;

                    @include portrait-tablet {
                        .name {
                            @include font-size(xs);
                        }
                    }
                    .name {
                        // @include font-size(xs);
                        font-size: 0.9rem;
                        line-height: 1rem;
                        font-family: $text-bold;
                        padding: 0;
                        padding-left: 8px;
                        max-height: 95%;
                        width: 100%;

                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                .search {
                    width: 100%;
                    display: none;
                    max-height: 40px;
                    margin: 0px !important;
                }

                .edit-columns,
                .add-report {
                    min-width: 45px;
                }
            }

            .content {
                overflow: auto;
                //height: calc(100vh - 200px);
                padding: 0px !important;

                .table {
                    tbody {
                        td {
                            padding: 10px 10px;
                            height: 80px;
                            @include font-size(xs);

                            &.col-icon {
                                padding-left: 20px;
                            }
                        }
                    }

                    th.column {
                        @include font-size(xs);
                        text-transform: uppercase;
                        font-family: $text;
                        color: $neutro-s80;
                    }
                }

                .datatable-container {
                    padding: 0px;
                    height: auto;
                    overflow-x: auto;

                    .arrow-container {
                        @include justify-content(flex-end);
                    }
                }
                .pagination {
                    width: 100%;
                    padding: 0px;
                    display: block;
                    grid-column: 1 / 4;
                    height: 45px;
                    padding: 8px 6px 0px;
                    // margin-top: 75px;
                    position: relative;
                    // top: -75px;
                    margin: 0px !important;

                    .item {
                        width: calc(50% - 5px);
                        height: 45px;
                        margin: 0 0px;
                        float: left;
                        @include border-radius(4px);
                        @extend .interaction;

                        &.next {
                            @include background($image: img('right_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                            float: right;
                            @extend .interaction;
                        }
                        &.prev {
                            @include background($image: img('left_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                        }
                    }
                }
            }
        }
    }
}
</style>
